import {YoutubePlayerState, minuteInMS} from "web-vcore";
import {GetLiveLocalSession, liveSession} from "../../../Engine/LiveSessionManagement.js";
import {LiveSession} from "../../../Engine/LiveSession.js";
import {SessionLog} from "../../../UI/Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {nativeBridge} from "../../../Utils/Bridge/Bridge_Native.js";
import {PlaySound_StopAll, PlaySound_StopAll_extraListeners, PlaySoundOpts, StopSound} from "../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {webAudioPlayerPool_noSession} from "../../../Utils/Media/WebAudioPlayer.js";
import {CycleCountType, SoundPanningType} from "./EffectPointer.js";
import {EffectPlayer, EffectPlayExtras} from "../../../Utils/EffectPlayers/EffectPlayer.js";
import {LightPlayer} from "../../../Utils/EffectPlayers/LightPlayer.js";
import {ScriptPlayer} from "../../../Utils/EffectPlayers/ScriptPlayer.js";
import {ShakePlayer} from "../../../Utils/EffectPlayers/ShakePlayer.js";
import {SoundPlayer} from "../../../Utils/EffectPlayers/SoundPlayer.js";
import {NarrateText_ForEngineComp, NotifyNonTTSUtteranceEnd, NotifyNonTTSUtteranceStart_FromSoundFile} from "../../../Utils/Services/TTS.js";
import {GetDreamingPeriodsFromSessionEvents, GetREMPeriodsFromDreamingPeriods} from "../../../UI/Timeline/Sessions/SessionUI/DetailsUI/DetailChart/DetailChartAnnotations.js";
import {GetCache} from "../../../Store/main/general/CacheState.js";
import {GetLights_WithUserTag} from "../lights.js";
import {LightType} from "../lights/@Light.js";
import {GetScripts_WithUserTag} from "../scripts.js";
import {GetShakes_WithUserTag} from "../shakes.js";
import {GetSounds_WithUserTag} from "../sounds.js";
import {EffectPointer} from "./EffectPointer.js";
import {SleepAsync} from "js-vextensions";
import {GeneralComp} from "../../../Engine/LiveSession/Components/GeneralComp.js";
import {GenerateUUID} from "mobx-graphlink";
import {NullifiedContextProvider} from "@dnd-kit/core/dist/components/DragOverlay/components/index.js";

export enum VLogLevel {
	VERBOSE = 2,
	DEBUG = 3,
	INFO = 4,
	WARN = 5,
	ERROR = 6,
	ASSERT = 7,
}
/** Useful for info that is not important enough to add session-events for, but which the user might still want to keep accessible for a while. (android log lines get saved to a rotating log-file) */
export function Android_VLog(level: VLogLevel, message: string, tag = "Android_VLog") {
	nativeBridge.Call("VLog_println", level, tag, message);
}

export class EffectPointerPlayer extends EffectPlayer {
	effectPointer: EffectPointer|n;
	activePlayIds = new Set<string>();

	soundPlayer = new SoundPlayer();
	shakePlayer = new ShakePlayer();
	lightPlayer = new LightPlayer();
	scriptPlayer = new ScriptPlayer();
	soundFile_startedPlayerIds = new Set<string>();

	/** Optional getter for a dynamic adjustment (in seconds) added to the spatial revolution-duration. */
	soundFile_spatial_revolutionDurationAdjustmentGetter?: ()=>number;
	/** Optional getter to override the cycle-count (eg. for CycleCountType.count_down_solveTimeLeft). Return null to suppress the announcement for that cycle. */
	soundFile_spatial_cycleComplete_countOverrideGetter?: ()=>number|null;
	/** Optional callback fired after each spatial audio cycle completes and the internal count has been updated. */
	soundFile_spatial_onCycleComplete?: (count: number)=>void;

	OnStart() {
		if (this.effectPointer?.soundEffectTag) {
			this.soundPlayer.sound = GetSounds_WithUserTag(this.effectPointer.soundEffectTag).Random();
		} /*else if (this.effectPointer?.tts?.soundTag) {
			GetSounds_WithUserTag(this.effectPointer.tts.soundTag).Random();
		}*/ else if (this.effectPointer?.shakeEffectTag) {
			this.shakePlayer.shake = GetShakes_WithUserTag(this.effectPointer.shakeEffectTag).Random();
		} else if (this.effectPointer?.lightEffectTag) {
			this.lightPlayer.light = GetLights_WithUserTag(this.effectPointer.lightEffectTag).Random();
		} else if (this.effectPointer?.scriptEffectTag) {
			this.scriptPlayer.script = GetScripts_WithUserTag(this.effectPointer.scriptEffectTag).Random();
		}
	}

	async Play(intensity?: number, extras?: Partial<EffectPlayExtras>): Promise<void> {
		if (this.effectPointer == null) return;
		const playId = GenerateUUID();
		this.activePlayIds.add(playId);
		try {
			return await this.Play_Advanced(this.effectPointer, playId, intensity, extras);
		} finally {
			this.activePlayIds.delete(playId);
		}
	}
	private async Play_Advanced(effect: EffectPointer, playId: string, intensity?: number, extras?: Partial<EffectPlayExtras>): Promise<void> {
		// if after-effect is set to fire immediately (-1), start it now before any awaits
		if (effect.afterEffect && effect.afterEffect_delay === -1) {
			void this.Play_Advanced(effect.afterEffect, playId, intensity, extras);
		}

		// effects in database
		if (effect.soundEffectTag) {
			if (this.soundPlayer.sound == null) return;
			if (!this.soundPlayer.prepared) {
				await this.soundPlayer.PrepareToPlay();
				/*if (this.promptSoundPlayer.youtubePlayer && this.c.loop) {
					this.promptSoundPlayer.youtubePlayer.loop = true;
				}*/
			}

			// if youtube-player sound, and it's already started (and looping), then just adjust volume
			if (this.soundPlayer.youtubePlayer && [YoutubePlayerState.PLAYING, YoutubePlayerState.BUFFERING].Contains(this.soundPlayer.youtubePlayer.state) && this.soundPlayer.youtubePlayer.loop) {
				this.soundPlayer.SetLiveVolume(intensity ?? 1);
				//this.UpdatePrompt_ForReducedIntensity();
			} else {
				await this.soundPlayer.Play(intensity, extras);
			}
		} else if (effect.shakeEffectTag) {
			await this.shakePlayer.Play(intensity, extras);
		} else if (effect.lightEffectTag) {
			await this.lightPlayer.Play(intensity, extras);
		} else if (effect.scriptEffectTag) {
			await this.scriptPlayer.Play(intensity, extras);
		}

		if (!this.activePlayIds.has(playId)) return;

		let stoppedEarly = false;

		// inline effects
		if (effect.soundFile) {
			const entry = effect.soundFile;
			const finalVolume_forSoundFile = (entry.volume ?? 1) * (intensity ?? 1);
			const pool = liveSession?.Comp(GeneralComp)?.webAudioPlayerPool ?? webAudioPlayerPool_noSession;

			const runCount = entry.runCount ?? 1;
			let currentPlayerId: string|n = null;
			const notifyStoppedEarly = ()=>{
				stoppedEarly = true;
				if (currentPlayerId) pool.StopSound(currentPlayerId);
			};
			this.Stop_extraListeners.push(notifyStoppedEarly);
			PlaySound_StopAll_extraListeners.push(notifyStoppedEarly);

			let cumulativeVolumeEvolveOffset = 0;
			for (let runI = 0; runI < runCount; runI++) {
				// use modulo, so that if run-count extends beyond defined configs (which is valid), we "repeat" the configs that are defined
				const runConfig = entry.runConfigs?.[runI % entry.runConfigs.length];
				const finalVolume_forRun = finalVolume_forSoundFile * (runConfig?.volumeMultiplier ?? 1);
				const audioFileToPlay = GetCache().audioFiles.filter(file=>{
					if (!file.filePath.includes(entry.filePath)) return false;
					if (runConfig?.subpathInclude && !file.filePath.toLowerCase().includes(runConfig.subpathInclude.toLowerCase())) return false;
					if (runConfig?.subpathExclude && file.filePath.toLowerCase().includes(runConfig.subpathExclude.toLowerCase())) return false;
					return true;
				}).Random();

				let startTime_resolved = 0; // in seconds
				if (entry.startTime) {
					// keep logic in-sync with Bridge_Audio.kt
					const minInMS_final = Math.trunc(entry.startTime.min * 1000).KeepBetween(0, audioFileToPlay.duration);
					const maxInMS_final = Math.trunc(entry.startTime.max * 1000).KeepBetween(0, audioFileToPlay.duration);
					const startTimeInMS = Math.trunc(minInMS_final + (Math.random() * (maxInMS_final - minInMS_final)));
					startTime_resolved = startTimeInMS / 1000;
				}

				if (audioFileToPlay) {
					// we must do this before, since the pool.PlaySound call awaits the entire sound-playing duration
					SessionLog(`Playing audio file: ${audioFileToPlay?.filePath} @runIndex:${runI}`);
					Android_VLog(VLogLevel.INFO, `Playing audio file: ${audioFileToPlay?.filePath} @runIndex:${runI}`);
					if (effect.recordSessionEvent && liveSession?.AsLocal) {
						liveSession.AsLocal.AddEvent({type: "EffectPointer.Play", effectPointer_play: {
							soundFile_filePath: audioFileToPlay?.filePath,
							soundFile_startTime: startTime_resolved,
						}});
					}

					const utteranceID = NotifyNonTTSUtteranceStart_FromSoundFile(audioFileToPlay.filePath, entry.wordsIgnoreGroup, Date.now());
					const playerId = GenerateUUID();
					currentPlayerId = playerId;
					this.soundFile_startedPlayerIds.add(playerId);
					const playResult = await pool.PlaySound({
						contentUri: audioFileToPlay.contentUri,
						playOpts: new PlaySoundOpts({
							volume: finalVolume_forRun,
							volume_evolveInterval: entry.volume_evolve ? entry.volume_evolveInterval : undefined,
							volume_evolveValues: entry.volume_evolve && entry.volume_evolveValues ? entry.volume_evolveValues.map(v=>v * (intensity ?? 1)) : undefined,
							volume_evolveOffset: entry.volume_evolveAcrossRuns ? cumulativeVolumeEvolveOffset : undefined,

							speed_multiplier_min: entry.speed_multiplier?.min,
							speed_multiplier_max: entry.speed_multiplier?.max,
							speed_duration: entry.speed_duration,

							//startTime_min: entry.startTime?.min
							//startTime_max: entry.startTime?.max,
							// switched to this approach, so JS side knows the exact start time (eg. for recording in session-event)
							startTime_min: startTime_resolved,
							startTime_max: startTime_resolved,

							durationLimit: entry.durationLimit,
							loop: entry.loop,
							panningType: entry.panningType,
							mutingType: entry.mutingType,
							playerId,
						}).Finalized(),
						spatial: entry.panningType == SoundPanningType.spatial ? {
							initialDirection_clockwise: entry.spatial_initialDirection_clockwise ?? true,
							switchDirInterval_min: entry.spatial_switchDirInterval_min ?? 3,
							switchDirInterval_max: entry.spatial_switchDirInterval_max ?? 12,
							revolutionDuration: entry.spatial_revolutionDuration ?? 10,
							distance: entry.spatial_distance ?? 0.3,
							revolutionDurationAdjustmentGetter: this.soundFile_spatial_revolutionDurationAdjustmentGetter,
							cycleComplete_countType: entry.spatial_cycleComplete_countType ?? CycleCountType.none,
							cycleComplete_countStart: entry.spatial_cycleComplete_countStart ?? 60,
							cycleComplete_countOverrideGetter: this.soundFile_spatial_cycleComplete_countOverrideGetter,
							onCycleComplete: this.soundFile_spatial_onCycleComplete,
						} : undefined,
					});
					currentPlayerId = null;
					if (utteranceID) NotifyNonTTSUtteranceEnd(utteranceID);
					if (entry.volume_evolveAcrossRuns) {
						cumulativeVolumeEvolveOffset += playResult.playDuration;
					}
				} else {
					SessionLog(`EffectPointerPlayer: No audio file found matching subpath filters + path: ${entry.filePath}`);
				}

				if (stoppedEarly) break;

				if (runConfig?.delay_post) {
					await SleepAsync(runConfig.delay_post * 1000);
				}

				if (stoppedEarly) break;
			}

			this.Stop_extraListeners.Remove(notifyStoppedEarly);
			PlaySound_StopAll_extraListeners.Remove(notifyStoppedEarly);
		}
		if (effect.tts) {
			const entry = effect.tts;
			const generalComp = liveSession?.Comp_ByString("GeneralComp") as GeneralComp|n;
			// atm, we only support TTS effects if an engine-session is active
			if (generalComp) {
				const volumeMultiplier = (entry.volume ?? 1) * (intensity ?? 1);
				const text_final = ApplyTTSTemplateVars(effect.tts.text, extras, liveSession);
				NarrateText_ForEngineComp(generalComp, text_final, volumeMultiplier, effect.tts.soundTag);
			}
		}

		if (effect.afterEffect && effect.afterEffect_delay !== -1 && !stoppedEarly && this.activePlayIds.has(playId)) {
			if (effect.afterEffect_delay) {
				await SleepAsync(effect.afterEffect_delay * 1000);
				if (!this.activePlayIds.has(playId)) return;
			}
			await this.Play_Advanced(effect.afterEffect, playId, intensity, extras);
		}
	}
	Stop_extraListeners = [] as Function[]; // eslint-disable-line
	async Stop() {
		// effects in database
		if (this.effectPointer?.soundEffectTag) {
			// we need to add an explicit call to Stop() here, with disownInternalPlayer=true, since the call in StopAlarm() does not do that disowning
			await this.soundPlayer.Stop(true);
		} else if (this.effectPointer?.shakeEffectTag) {
			await this.shakePlayer.Stop();
		} else if (this.effectPointer?.lightEffectTag) {
			await this.lightPlayer.Stop();
			if (this.lightPlayer.light?.type == LightType.Screen && liveSession?.resetScreenLightPlayer.light) {
				liveSession.resetScreenLightPlayer.Play();
			}
		} else if (this.effectPointer?.scriptEffectTag) {
			await this.scriptPlayer.Stop();
		}

		// inline effects
		for (const playerId of this.soundFile_startedPlayerIds) {
			StopSound(playerId);
		}
		this.soundFile_startedPlayerIds.clear(); // just an optimization, in case Play() and Stop() are called many times on the some EffectPointerPlayer
		this.activePlayIds.clear();

		this.Stop_extraListeners.forEach(a=>a());
	}

	NotifyReducedIntensity(intensity: number) {
		if (this.effectPointer?.soundEffectTag) {
			this.soundPlayer.SetLiveVolume(intensity);
		} else if (this.effectPointer?.shakeEffectTag) {
			// do nothing (we can't adjust an existing shake-prompt, and duration too short for weaker-restart to be worthwhile)
		} else if (this.effectPointer?.lightEffectTag) {
			if (this.lightPlayer.light?.temp) {
				// if existing prompt is still playing, then re-play with the new (lower) intensity
				//	(else, do nothing, since we don't want intensity-decay to ever increase effect noticability)
				if (this.lightPlayer.playState != "stopped") {
					this.Play(intensity);
				}
			} else {
				// just play light-config again; will just update the intensity
				this.Play(intensity);
			}
		} else if (this.effectPointer?.scriptEffectTag) {
			// if existing prompt is still playing, then re-play with the new (lower) intensity
			//	(else, do nothing, since we don't want intensity-decay to ever increase effect noticability)
			if (this.scriptPlayer.playState != "stopped") {
				this.Play(intensity);
			}
		}
	}
}

export function ApplyTTSTemplateVars(text: string, extras: Partial<EffectPlayExtras>|n, session: LiveSession|n): string {
	// this parses substrings within "$rand{...|...}" or "$seq{...|...}", and selects one (random, or along sequence)
	const randRegex = /\$rand\{(.+?)\}/g;
	text = text.replace(randRegex, (match, g1: string)=>{
		const substrings = g1.split("|");
		return substrings.Random();
	});
	const seqRegex = /\$seq\{(.+?)\}/g;
	text = text.replace(seqRegex, (match, g1: string)=>{
		const substrings = g1.split("|");
		const playIndexDuringThisSequence = extras?.playCountThisSequence != null ? extras.playCountThisSequence - 1 : 0;
		const substringIndex = playIndexDuringThisSequence % substrings.length;
		return substrings[substringIndex];
	});

	// this applies variable substitutions, based on the tts-var values in the "extras" object
	for (const [key, value] of Object.entries(extras?.ttsVars ?? {})) {
		text = text.replace(`$${key}`, value);
	}

	if (session) {
		text = ApplySessionTemplateVars(text, session);
	}

	return text;
}

export function ApplySessionTemplateVars(text: string, session: LiveSession): string {
	const toMinsStr = (ms: number)=>(ms / minuteInMS).RoundTo_Str(1);
	const dreamPeriods = GetDreamingPeriodsFromSessionEvents(session.coreData.events, true);
	const totalDreamingDuration = dreamPeriods.reduce((sum, period)=>sum + period.duration, 0);
	const remPeriods = GetREMPeriodsFromDreamingPeriods(dreamPeriods);

	const activeVariant = session.c.variants.variants.find(a=>a.name == session.coreData.variantName);
	const variantNameForTTS = (activeVariant?.nameForTTS?.trim() || activeVariant?.name) ?? "";

	// time-derived variables
	const now_date = new Date();
	const now_minutes = (now_date.getHours() * 60) + now_date.getMinutes();
	const closestHour = Math.round(now_minutes / 60) % 24;
	const hourAndMinute_h = now_date.getHours();
	const hourAndMinute_m = now_date.getMinutes().toString().padStart(2, "0");
	// note: order must have "$hour" last, to avoid prefix-collision
	text = text.replaceAll("$hourAndMinute", `${hourAndMinute_h}:${hourAndMinute_m}`);
	text = text.replaceAll("$hour", closestHour.toString());

	// non-numbered session variables
	text = text.replaceAll("$variantName", variantNameForTTS);
	text = text.replaceAll("$dreamDurations", dreamPeriods.map(period=>toMinsStr(period.duration)).join(", "));
	text = text.replaceAll("$dreamingTotal", toMinsStr(totalDreamingDuration));
	text = text.replaceAll("$remDurations", remPeriods.map(period=>toMinsStr(period.dreamingDuration)).join(", "));

	// numbered session variables
	for (let n = 1; n <= 9; n++) {
		text = text.replaceAll(`$dreamN${n}_preGap`, dreamPeriods[dreamPeriods.length - n] ? toMinsStr(dreamPeriods[dreamPeriods.length - n].preGap) : "none");
		text = text.replaceAll(`$dreamN${n}_duration`, dreamPeriods[dreamPeriods.length - n] ? toMinsStr(dreamPeriods[dreamPeriods.length - n].duration) : "none");
		text = text.replaceAll(`$remN${n}_preGap`, remPeriods[remPeriods.length - n] ? toMinsStr(remPeriods[remPeriods.length - n].preGap) : "none");
		text = text.replaceAll(`$remN${n}_duration`, remPeriods[remPeriods.length - n] ? toMinsStr(remPeriods[remPeriods.length - n].dreamingDuration) : "none");
		text = text.replaceAll(`$remN${n}_dreams`, remPeriods[remPeriods.length - n] ? remPeriods[remPeriods.length - n].dreamCount.toString() : "none");
	}

	return text;
}